import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import api, { setReadOnlyChecker, setReadOnlyReasonProvider, setPinPromptRequester, getImpersonationToken, setImpersonationToken, clearSessionExpiry } from "./api";
import { clearAll } from "./offlineDb";
import { clearSWCaches } from "./swRegister";

const AuthContext = createContext(null);
const EMPTY_PREFS = {};

// Feb 2026 · Rollen-View-Switcher (3-Wege) + Concierge-Impersonation.
// -------------------------------------------------------------------
// Echte Rolle (`user.role`) kommt aus dem JWT — der Backend enforced sie
// immer. Für die UI kann ein Admin die Perspektive umschalten, um zu sehen,
// was ein Concierge oder Kunde tatsächlich sieht.
//
// Ansicht-Persistenz: Feb 2026 · User-Directive
//   „Nach dem Reload kehrt der Admin automatisch zur Admin-Ansicht zurück."
// → Die effektive Rolle wird bewusst NICHT in localStorage persistiert.
// Sie lebt nur im React-State des Providers und wird beim Reload (harter
// Refresh / Session-Wiederherstellung) auf `user.role` zurückgesetzt.
// Damit ist der Admin nach jedem Fenster-Start wieder in seiner echten
// Admin-Ansicht — Impersonationen sind bewusste, kurzlebige Vorgänge.
//
// Downgrade-Regeln (Sicherheit):
//   real=admin      → darf effectiveRole ∈ { admin, concierge, customer }
//   real=concierge  → immer concierge (kein Switcher — Feb 2026 · User-
//                     Directive „ausschließlich für Accounts mit Rolle
//                     admin sichtbar")
//   real=customer   → immer customer (kein Switcher)
//   real=family     → immer family
//   real=partner    → immer partner
//
// Concierge-Impersonation (Feb 2026 · Admin-Feature):
//   Wenn ein Admin die Concierge-Perspektive wählt, muss er zusätzlich
//   den konkreten Concierge auswählen — dessen Zuordnungen bestimmen den
//   Daten-Filter (Anfragen `assigned_to`, Kalender `assignee_id`).
//   `impersonatedConcierge = { id, name }` lebt ebenfalls nur im
//   React-State (kein localStorage) und ist damit reload-flüchtig.
//   Für echte Concierges wird `activeConciergeId` automatisch auf ihre
//   eigene id gesetzt (siehe useMemo weiter unten).
function clampEffective(real, wanted) {
  if (!real) return null;
  if (real === "admin") {
    if (wanted === "admin" || wanted === "concierge" || wanted === "customer") return wanted;
    return "admin";
  }
  // Feb 2026 · Nur Admins dürfen wechseln — reale Concierges bleiben
  // Concierge (kein View-Switcher sichtbar).
  return real;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [checking, setChecking] = useState(true);
  // Feb 2026 · Reload-Reset · effectiveRole startet immer als null →
  // fällt auf `user.role` zurück (Admin sieht Admin-Ansicht). Ansicht
  // ist reine React-State-Persistenz innerhalb einer Session.
  const [effectiveRoleRaw, setEffectiveRoleRaw] = useState(null);
  const [impersonatedConcierge, setImpersonatedConciergeState] = useState(null);
  // Feb 2026 · Directive „PIN-Gate für Fremd-Concierge-Bearbeitung".
  // Session-scoped Unlock-Flag — wird beim Wechsel des impersonierten
  // Concierges (inkl. Reset auf null) automatisch zurückgesetzt.
  // Kein localStorage → nach Reload muss die PIN erneut eingegeben werden.
  const [otherConciergeUnlocked, setOtherConciergeUnlocked] = useState(false);
  // Der PIN-Dialog wird von der Layout-Komponente gerendert. Über dieses
  // Flag hier hebt der Interceptor (via `setPinPromptRequester`) den
  // Dialog an, sobald ein Write in einer Fremd-Concierge-Ansicht blockiert
  // wurde.
  const [pinPromptOpen, setPinPromptOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(false);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const logout = async () => {
    // Kunden-Ansicht (Tab-Token): nur den Tab-Token verwerfen — der
    // Server-Logout würde sonst den Admin-Cookie im selben Browser löschen.
    if (getImpersonationToken()) {
      setImpersonationToken(null);
    } else {
      try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    }
    setEffectiveRoleRaw(null);
    setImpersonatedConciergeState(null);
    setUser(false);
    // 22 Sep 2026 · Lokalen Cache beim Logout löschen (DSGVO).
    try { await clearAll(); } catch { /* ignore */ }
    try { clearSWCaches(); } catch { /* ignore */ }
    try { clearSessionExpiry(); } catch { /* ignore */ }
  };

  // Serverseitige UI-Präferenzen (users.ui_prefs) — ersetzt localStorage,
  // damit mehrere Accounts im selben Browser sich nicht gegenseitig
  // überschreiben. Optimistisch im State, PATCH best-effort.
  const uiPrefs = useMemo(() => (user && user.ui_prefs) || EMPTY_PREFS, [user]);
  const setUiPrefs = useCallback((patch) => {
    setUser((u) => (u ? { ...u, ui_prefs: { ...(u.ui_prefs || {}), ...patch } } : u));
    api.patch("/me/ui-prefs", patch).catch(() => { /* read-only / offline */ });
  }, []);
  // Ausgewählte Keys (oder alle) auf Standard zurücksetzen.
  const resetUiPrefs = useCallback(async (keys = []) => {
    setUser((u) => {
      if (!u) return u;
      const next = { ...(u.ui_prefs || {}) };
      (keys.length ? keys : Object.keys(next)).forEach((k) => { delete next[k]; });
      return { ...u, ui_prefs: next };
    });
    await api.delete("/me/ui-prefs", { params: keys.length ? { keys: keys.join(",") } : {} });
  }, []);

  const effectiveRole = useMemo(
    () => clampEffective(user?.role, effectiveRoleRaw || user?.role),
    [user?.role, effectiveRoleRaw],
  );

  const setEffectiveRole = useCallback((role) => {
    // Feb 2026 · State-only. KEIN localStorage-Write mehr — Reload setzt
    // die Ansicht automatisch auf `user.role` zurück (User-Directive).
    setEffectiveRoleRaw(clampEffective(user?.role, role));
  }, [user?.role]);

  // Ref-Spiegel des Unlock-Flags + des impersonierten Concierges — wir
  // brauchen die aktuellen Werte in stabilen Callbacks, ohne diese bei
  // jedem Toggle neu zu erzeugen (Endlos-Renderschleifen bei useEffect-Deps).
  const otherConciergeUnlockedRef = useRef(false);
  useEffect(() => { otherConciergeUnlockedRef.current = otherConciergeUnlocked; }, [otherConciergeUnlocked]);
  const impersonatedConciergeRef = useRef(null);
  useEffect(() => { impersonatedConciergeRef.current = impersonatedConcierge; }, [impersonatedConcierge]);

  const setImpersonatedConcierge = useCallback((entry) => {
    // entry = { id, name } oder null (zum Löschen). Reload-flüchtig.
    const safe = entry && entry.id ? { id: entry.id, name: entry.name || "" } : null;
    // Feb 2026 · Directive „PIN-Audit-Log": Wenn wir aktuell in einer
    // Fremd-Concierge-Ansicht sind UND freigeschaltet UND jetzt gewechselt
    // wird, loggen wir einen impliziten Relock — sonst sieht der Admin
    // im Log irgendwann eine Unlock-Zeile ohne dazugehörigen Relock.
    setImpersonatedConciergeState((prev) => {
      if (prev?.id && (!safe || safe.id !== prev.id)) {
        // Prüfen ob vorher freigeschaltet — sonst kein Log nötig.
        if (otherConciergeUnlockedRef.current) {
          api.post("/team/pin-audit/relock", {
            impersonated_concierge_id: prev.id,
            impersonated_concierge_name: prev.name || "",
            reason: "impersonation-changed",
          }).catch(() => { /* best-effort */ });
        }
      }
      return safe;
    });
    // Feb 2026 · Directive „PIN-Gate": Jeder Wechsel des impersonierten
    // Concierges resettet den Unlock — auch das Zurücksetzen auf null.
    // So muss der Admin, der zwei fremde Concierges nacheinander editiert,
    // für jeden separat die PIN eingeben (Audit-Klarheit).
    setOtherConciergeUnlocked(false);
    setPinPromptOpen(false);
  }, []);

  const unlockOtherConcierge = useCallback(() => setOtherConciergeUnlocked(true), []);
  const lockOtherConcierge = useCallback(() => {
    // Feb 2026 · Directive „PIN-Audit-Log": Manuelles „Wieder sperren"
    // loggen. Nur wenn wir tatsächlich freigeschaltet WAREN — sonst wäre
    // die Zeile Rauschen.
    setOtherConciergeUnlocked((prev) => {
      if (prev && impersonatedConciergeRef.current?.id) {
        api.post("/team/pin-audit/relock", {
          impersonated_concierge_id: impersonatedConciergeRef.current.id,
          impersonated_concierge_name: impersonatedConciergeRef.current.name || "",
          reason: "manual",
        }).catch(() => { /* best-effort */ });
      }
      return false;
    });
  }, []);
  const requestPinPrompt = useCallback(() => setPinPromptOpen(true), []);
  const closePinPrompt = useCallback(() => setPinPromptOpen(false), []);

  // Aktiver Concierge-Filter — treibt die Datenfilterung in TeamRequests,
  // TeamCalendar etc.
  //   • Echter Concierge → immer die eigene id (automatischer Filter).
  //   • Echter Admin mit effectiveRole=concierge → impersonatedConcierge.id.
  //   • Alles andere → null (kein Filter).
  const activeConciergeId = useMemo(() => {
    if (user?.role === "concierge") return user.id;
    if (user?.role === "admin" && effectiveRole === "concierge") {
      return impersonatedConcierge?.id || null;
    }
    return null;
  }, [user?.role, user?.id, effectiveRole, impersonatedConcierge]);

  const activeConciergeName = useMemo(() => {
    if (user?.role === "concierge") return user.name || user.email || "";
    if (user?.role === "admin" && effectiveRole === "concierge") {
      return impersonatedConcierge?.name || "";
    }
    return "";
  }, [user, effectiveRole, impersonatedConcierge]);

  // Feb 2026 · Read-only-Modus für Admin-Impersonation.
  // -----------------------------------------------------------
  // Zwei Impersonations-Kanäle sind aktiv:
  //   1) Concierge-Ansicht: gleicher Tab, Admin-JWT. Erst aktiv wenn ein
  //      konkreter Concierge gewählt wurde (impersonatedConcierge.id).
  //      Damit erscheint der Banner NICHT auf der Picker-Seite selbst,
  //      wo der Admin sich noch entscheidet.
  //   2) Kunden-Ansicht: neuer Tab, `type=customer_impersonation`-Cookie.
  //      Der Backend blockt alle Writes systemweit mit 403.
  //      `user._impersonation` kommt aus `/auth/me` und trägt Actor-Info.
  //
  // Feb 2026 · Directive „Admin-eigene Concierge-Ebene": Wenn der Admin
  // SICH SELBST als Concierge auswählt (impersonatedConcierge.id ===
  // user.id), ist das keine Impersonation im read-only-Sinn — er sieht
  // seine eigene Concierge-Perspektive (nur zugewiesene Objekte + Tasks)
  // und darf voll editieren. Nur wenn er einen ANDEREN Concierge wählt,
  // greift der Read-only-Interceptor.
  const isImpersonatingConcierge = user?.role === "admin"
    && effectiveRole === "concierge"
    && !!impersonatedConcierge?.id
    && impersonatedConcierge.id !== user.id;
  const isSelfConciergeView = user?.role === "admin"
    && effectiveRole === "concierge"
    && !!impersonatedConcierge?.id
    && impersonatedConcierge.id === user.id;
  const customerImpersonation = user?._impersonation && user._impersonation.type === "customer"
    ? user._impersonation
    : null;
  const isImpersonatingCustomer = !!customerImpersonation;
  const isImpersonating = isImpersonatingConcierge || isImpersonatingCustomer;
  // `isReadOnly` steuert den globalen axios-Interceptor.
  // Feb 2026 · Directive „PIN-Gate": Fremd-Concierge-Ansicht ist nur so
  // lange read-only, bis der Admin die 4-stellige Bearbeitungs-PIN
  // eingegeben hat. Kunden-Impersonation bleibt IMMER read-only (die
  // Regel „Kunde erlebt genau seine Sicht" darf nicht durch eine PIN
  // ausgehebelt werden).
  const isReadOnly = (isImpersonatingConcierge && !otherConciergeUnlocked)
    || isImpersonatingCustomer;
  const readOnlyReason = isImpersonatingConcierge && !otherConciergeUnlocked
    ? "other-concierge"
    : "impersonation";

  // Registriere den Read-only-Getter beim Axios-Interceptor. useEffect
  // sorgt dafür, dass der Interceptor immer den aktuellen Wert liest,
  // ohne dass die api.js selbst React-State kennt.
  useEffect(() => {
    setReadOnlyChecker(() => isReadOnly);
    return () => setReadOnlyChecker(() => false);
  }, [isReadOnly]);

  useEffect(() => {
    setReadOnlyReasonProvider(() => readOnlyReason);
    return () => setReadOnlyReasonProvider(() => "impersonation");
  }, [readOnlyReason]);

  useEffect(() => {
    setPinPromptRequester(() => requestPinPrompt);
    return () => setPinPromptRequester(null);
  }, [requestPinPrompt]);

  return (
    <AuthContext.Provider value={{
      user, setUser, checking, refresh, logout,
      effectiveRole, setEffectiveRole,
      impersonatedConcierge, setImpersonatedConcierge,
      activeConciergeId, activeConciergeName,
      isImpersonating, isImpersonatingConcierge, isImpersonatingCustomer,
      isSelfConciergeView,
      otherConciergeUnlocked, unlockOtherConcierge, lockOtherConcierge,
      pinPromptOpen, requestPinPrompt, closePinPrompt,
      customerImpersonation, isReadOnly,
      uiPrefs, setUiPrefs, resetUiPrefs,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
